import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { categoryName, formatPrice } from "../lib/catalog";
import type { Dorama } from "../types";

export function CatalogSearch() {
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Dorama[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    const timeout = window.setTimeout(() => {
      setLoading(true);
      supabase
        .from("doramas")
        .select("*")
        .ilike("title", `%${term}%`)
        .eq("published", true)
        .limit(6)
        .then(({ data }) => {
          setResults((data || []) as Dorama[]);
          setLoading(false);
        });
    }, 180);
    return () => window.clearTimeout(timeout);
  }, [query]);

  const choose = (dorama: Dorama) => {
    setOpen(false);
    setQuery("");
    navigate(`/dorama/${dorama.slug || dorama.id}`);
  };

  return (
    <div className={`catalog-search ${open ? "open" : ""}`} ref={rootRef}>
      {open && (
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => event.key === "Escape" && setOpen(false)}
          placeholder="Buscar doramas..."
          aria-label="Buscar doramas"
        />
      )}
      <button
        className="header-icon-button"
        type="button"
        aria-label={open ? "Fechar busca" : "Abrir busca"}
        onClick={() => {
          setOpen((current) => !current);
          window.setTimeout(() => inputRef.current?.focus(), 0);
        }}
      >{open ? (
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
      ) : (
        <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
      )}</button>
      {open && query.trim().length >= 2 && (
        <div className="search-results" role="listbox" aria-label="Resultados da busca">
          {loading && <p>Buscando…</p>}
          {!loading && results.length === 0 && <p>Nenhum título encontrado.</p>}
          {results.map((dorama) => (
            <button type="button" key={dorama.id} onClick={() => choose(dorama)}>
              {dorama.cover_url ? <img src={dorama.cover_url} alt="" /> : <span className="search-thumb" />}
              <span><strong>{dorama.title}</strong><small>{categoryName(dorama.category)} · {formatPrice(dorama.price)}</small></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
